import * as GC from '@grapecity/spread-sheets';
import { arrayContainsOnlyAllowedText, arrayContainsOnlyDates, arrayContainsOnlyNumbers, getDuplicatedValues, getUnique, hasDuplicates, isEmpty } from '@helpers/arrays';
import { LookupData, SpreadLookupInfo } from '@models/lookup-data';
import { LookupDataValidation } from '@models/lookup-data-validation';
import { LookupDuplicatedCellsReport } from '@models/lookup-duplicated-cells-report';

/**
 * @param {*} tables An array of tables for the same Mnemonic
 * @param {*} dimensions An array with the table dimensions (e.g. ['D1_Text', 'D2_Numeric', 'D3_Text'])
 * @description `Validation`
 *  * Checks for missing records for a Dimension3+Dimension1+Dimension2 combination and inserts new ones with Result = 0.
 *  * Sorts the list by `[Dimension 3], Dimension 1, Dimension 2`
 *  * Returns whether the data is valid, the invalid Mnemonic Id (if any) and the validationMessage
 *
 *  **Validations:**
 *
 *  1. Verify all tables have same *Mnemonic*
 *
 *  + **For All Tables**
 *      + All Results must be numeric
 *      + User can only enter *Numbers* in Numeric dimensions
 *      + User can only enter *Dates* in Date dimensions
 *      + If Missing Results, fill them with 0's (after adding a new Header)
 *      + After removing a Header, saving table will remove orphaned results (the ones left without header)
 *
 *  + **If *Mnemonic* is 2D**
 *      + 3rd dimension must be empty
 *
 *  + **If *Mnemonic* is 3D**
 *      + Verify all 3rd dimensions are unique
 *      + Verify no 3rd dimension is empty
 *      + All tables have the same 1st dimensions (length and values)
 *      + All tables have the same 2nd dimensions (length and values)
 *
 *  + **For 1D tables**
 *      + 2nd and 3rd dimensions must be empty
 *  	  + Verify all 1st dimension values are unique
 *      + Dim1 headers cannot be empty if table has Results
 *
 *  + **For all 2D and 3D tables**
 *  	  + Verify all 1st dimensions are unique
 *      + Verify all 2nd dimensions are unique
 *      + Dim1 and Dim2 headers cannot be empty if table has Results
 *
 */
const tableValidation = new LookupDataValidation();
export const addValidationError = (error: string): boolean => {
  tableValidation.validationMessages.push(error);
  return false;
};

export const removeValidationError = (error: string, mnemonic: string = null): boolean => {
  const messagesFiltered = tableValidation.validationMessages.filter(element =>
    mnemonic ? !element.includes(error) || !element.includes(mnemonic) : !element.includes(error)
  );

  tableValidation.validationMessages = messagesFiltered;
  return true;
};

export const getNonDuplicateMessages = (messageArray = tableValidation.validationMessages): string[] => {
  const messageSet = new Set(messageArray);
  return Array.from(messageSet);
};

export const isValidLookupData = (dataTables: LookupData[], infoTables: SpreadLookupInfo[]): LookupDataValidation => {
  tableValidation.rangeToBeDeleted = null;
  const [dimension1, dimension2, dimension3] = infoTables[0].references;
  const mnemonic = infoTables[0].mnemonic;
  const numberOfDimensions = infoTables[0].references.length;

  // Data type validation
  let isValid = true;
  infoTables.forEach(table => {
    if (table.previousData) {
      switch (table.previousData.dimension) {
        case 'rows':
          isValid = isValid && validateAxis(dimension1, table.rows, mnemonic);
          break;
        case 'cols':
          isValid = isValid && validateAxis(dimension2, table.cols, mnemonic);
          break;
        case 'pivots':
          isValid = isValid && validateAxis(dimension3, table.allThirdDimensions, mnemonic);
          break;
        case 'results':
          const resultsNumeric = table.results.every(resultRow => arrayContainsOnlyNumbers(resultRow));
          if (resultsNumeric) {
            removeValidationError('Non numeric value in a Result cell', mnemonic);
          } else {
            addValidationError(`(${table.mnemonic}) Non numeric value in a Result cell`);
          }
          isValid = isValid && resultsNumeric;
      }
    }
  });

  tableValidation.isValidTable = isValid || false;

  //
  //  ✅ Verify all tables have same Mnemonic
  //
  // let thisMnemonic = null;
  const mnemonics = dataTables.map(d => d.Mnemonic).filter(getUnique);
  if (mnemonics.length > 1) {
    tableValidation.isValidTable = false;
    addValidationError(`Cannot edit different tables at the same time (${mnemonics.join(', ')})`);
  }
  // else {
  //   thisMnemonic = mnemonics[0];
  // }

  //
  // ✅ If Mnemonic is 1D, then 2nd and 3rd dimensions must be empty
  //
  if (numberOfDimensions === 1) {
    if (!isEmpty(dimension2)) {
      tableValidation.isValidTable = false;
      addValidationError(`(${mnemonic}) Dimension 2 must be empty in a 1D table (found ${dimension2})`);
    }
    if (!isEmpty(dimension3) || !isEmpty(infoTables[0].thirdDimension)) {
      tableValidation.isValidTable = false;
      addValidationError(`(${mnemonic}) Dimension 3 must be empty in a 1D table (found ${dimension3})`);
    }
  }

  //
  // ✅ If Mnemonic is 2D, then 3rd dimension must be empty
  //
  if (numberOfDimensions === 2 && (!isEmpty(dimension3) || !isEmpty(infoTables[0].thirdDimension))) {
    tableValidation.isValidTable = false;
    addValidationError(`(${mnemonic}) Dimension 3 must be empty in a 2D table (found ${dimension3})`);
  }

  //
  //  ✅ If Mnemonic is 3D
  //
  if (numberOfDimensions === 3) {
    const lastTable = infoTables[infoTables.length - 1];
    const pivots = [...lastTable.allThirdDimensions].filter(v => v !== null);
    const duplicates = getDuplicatedValues(pivots);
    // console.log({ dataTables, infoTables, pivots, hasDuplicates: hasDuplicates(pivots), duplicates });

    // ✔ Verify all 3rd dimensions are unique
    if (hasDuplicates(pivots)) {
      tableValidation.isValidTable = false;
      removeValidationError('found in 3rd dimension', mnemonic);
      addValidationError(
        `(${mnemonic}) Duplicated ${duplicates.length > 1 ? 'values' : 'value'} found in 3rd dimension: ${duplicates.join(', ')} `
      );
    } else {
      removeValidationError('found in 3rd dimension', mnemonic);
    }

    // ✔ Verify no 3rd dimension is empty
    //    Disabling this check as emptied 3rd dimensions will flag this 3rd dimension for deletion
    // if (pivots.some(v => isEmpty(v))) {
    //   tableValidation.isValidTable = false;
    //   addValidationError(`You cannot have empty 3rd dimensions.`);
    // } else {
    //   removeValidationError('empty 3rd dimension');
    // }

    // ✔ All tables have the same 1st dimensions (length and values)
    if (!tablesHaveSameRows(infoTables)) {
      tableValidation.isValidTable = false;
      addValidationError(`(${mnemonic}) Tables have mismatching rows.`);
    } else {
      removeValidationError('mismatching rows', mnemonic);
    }

    // ✔ All tables have the same 2nd dimensions (length and values)

    if (!tablesHaveSameCols(infoTables)) {
      tableValidation.isValidTable = false;
      addValidationError(`(${mnemonic}) Tables have mismatching columns.`);
    } else {
      removeValidationError('mismatching columns', mnemonic);
    }
  }

  const dupCells: LookupDuplicatedCellsReport = hasDuplicatedCells(dataTables, infoTables[0].references);

  if (dupCells.duplicatedFound) {
    tableValidation.isValidTable = false;
    tableValidation.invalidMnemonicId = dupCells.duplicatedMnemonicId;
    addValidationError(`(${mnemonic}) Cannot have duplicated dimensions: ${dupCells.duplicatedKey}`);
  } else {
    removeValidationError('duplicated dimensions', mnemonic);
  }

  tableValidation.validationMessages = getNonDuplicateMessages(tableValidation.validationMessages);

  return tableValidation;
};

const hasDuplicatedCells = (tables: LookupData[], dimensions: any[]): LookupDuplicatedCellsReport => {
  const [dimension1, dimension2, dimension3] = dimensions;
  const numberOfDimensions = dimensions.length;

  const result = new LookupDuplicatedCellsReport();
  const foundCells = {};

  for (let i = 0; i < tables.length; i++) {
    const table = tables[i];

    let key;
    if (numberOfDimensions === 1 && !isEmpty(dimension1)) {
      key = `Row-[${table[dimension1]}]`;
    } else if (numberOfDimensions === 2 && !isEmpty(dimension2)) {
      key = `Row-[${table[dimension1]}]--Column-[${table[dimension2]}]`;
    } else if (!isEmpty(dimension3)) {
      key = `Pivot-[${table[dimension3]}]--Row-[${table[dimension1]}]--Column-[${table[dimension2]}]`;
    }

    if (!foundCells[key] && !key.includes(`null`)) {
      foundCells[key] = `${key}---> ${table.Result}`;
    } else if (!key.includes(`null`)) {
      result.duplicatedFound = true;
      result.duplicatedMnemonicId = table.Mnemonic_Id;
      result.duplicatedKey = key;
      return result;
    }
  }
  return result;
};

// const sortTablesByDimensions = (tables: LookupData[], dimensions: any[]) => {
//   const [dim1, dim2, dim3] = dimensions;

//   if (dimensions.length === 3) {
//     tables.sort(compareKeys([dim3, dim1, dim2]));
//   } else if (dimensions.length === 2) {
//     tables.sort(compareKeys([dim1, dim2]));
//   } else if (dimensions.length === 1) {
//     tables.sort(compareKeys([dim1]));
//   }
// };

// Spread Validation
export const NUMBERVALIDATOR = GC.Spread.Sheets.DataValidation.createNumberValidator(null, null, null, false);

export const setNumberValidation = (range: GC.Spread.Sheets.CellRange) => range.validator(NUMBERVALIDATOR);

export const NOEMPTYSTRINGVALIDATOR = GC.Spread.Sheets.DataValidation.createTextLengthValidator(
  GC.Spread.Sheets.ConditionalFormatting.ComparisonOperators.greaterThan,
  '0',
  ''
);

export const validateAxis = (dataType: string, arr: any[], mnemonic: string): boolean => {
  const SPEC_CHAR_MSG = 'Cannot have special characters in a Text dimension';
  const NOT_NUMERIC_MSG = 'Cannot have non-numeric values in a Numeric only dimension';
  const NOT_DATE_MSG = 'Cannot have non-date values in a Date only dimension and date must be in US format MM/DD/YYYY';

  if (dataType.match(/D[123]_Text/i)) {
    if (arrayContainsOnlyAllowedText(arr)) {
      return removeValidationError(SPEC_CHAR_MSG, mnemonic);
    }
    return addValidationError(`(${mnemonic}) ${SPEC_CHAR_MSG}`);
  }
  if (dataType.match(/D[123]_Numeric/i)) {
    if (arrayContainsOnlyNumbers(arr)) {
      return removeValidationError(NOT_NUMERIC_MSG, mnemonic);
    }
    return addValidationError(`(${mnemonic}) ${NOT_NUMERIC_MSG}`);
  }
  if (dataType.match(/D[123]_Date/i)) {
    if (arrayContainsOnlyDates(arr)) {
      return removeValidationError(NOT_DATE_MSG, mnemonic);
    }
    return addValidationError(`(${mnemonic}) ${NOT_DATE_MSG}`);
  }
  return true;
};

export const isValidValue = (dataType: string, value: any) => {
  if (dataType.match(/D[123]_Text/i)) {
    return arrayContainsOnlyAllowedText([value]);
  }

  if (dataType.match(/D[123]_Numeric/i)) {
    return arrayContainsOnlyNumbers([value]);
  }

  if (dataType.match(/D[123]_Date/i)) {
    return arrayContainsOnlyDates([value]);
  }

  return true;
};

export const tablesHaveSameRows = (tables: SpreadLookupInfo[]) => new Set(tables.map(t => t.rows.toString())).size === 1;
export const tablesHaveSameCols = (tables: SpreadLookupInfo[]) => new Set(tables.map(t => t.cols.toString())).size === 1;
